import React from 'react';
import styled from 'styled-components';
import { type FallbackProps, ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { FiAlertTriangle } from 'react-icons/fi';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary?: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error }) => {
  return (
    <ErrorContainer>
      <ErrorIcon>
        <FiAlertTriangle />
      </ErrorIcon>
      <ErrorContent>
        <ErrorTitle>Something went wrong</ErrorTitle>
        <ErrorMessage>{error.message}</ErrorMessage>
      </ErrorContent>
    </ErrorContainer>
  );
};

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children, fallback = ErrorFallback }) => {
  return (
    <ReactErrorBoundary
      FallbackComponent={fallback as unknown as React.ComponentType<FallbackProps>}
      onError={(error, errorInfo) => {
        // Log error for debugging (optional)
        console.error('ErrorBoundary caught an error:', error, errorInfo);
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 8px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  margin: 16px 0;
`;

const ErrorIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #dc2626;
  font-size: 20px;
  margin-right: 12px;
  flex-shrink: 0;
`;

const ErrorContent = styled.div`
  flex: 1;
`;

const ErrorTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 600;
  color: #991b1b;
`;

const ErrorMessage = styled.p`
  margin: 0;
  font-size: 13px;
  color: #7f1d1d;
  line-height: 1.4;
`;
